import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Eye, MapPin, X } from 'lucide-react';
import { formatCurrency, formatDate, formatRelativeTime } from '@/lib/utils';
import { ROUTES, buildRoute } from '@/constants/routes';
import { useBookings, useCancelBooking } from '../hooks/useBookings';
import type { BookingsParams } from '../api/bookings.api';
import { useFilters } from '@/hooks/useFilters';
import { usePagination } from '@/hooks/usePagination';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { FilterBar } from '@/components/common/FilterBar';
import { BookingStatusBadge, PaymentStatusBadge } from '@/components/common/StatusBadge';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dropdown, DropdownItem, DropdownSeparator } from '@/components/ui/dropdown';
import { TablePagination } from '@/components/ui/pagination';
import type { Booking } from '@/types';

// Matches the backend's BookingStatus enum (booking.entity.ts).
const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'driver_assigned', label: 'Driver Assigned' },
  { value: 'ongoing', label: 'In Transit' },
  { value: 'completed', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Matches the backend's VehicleType enum (vehicle.entity.ts).
const vehicleOptions = [
  { value: 'all', label: 'All Vehicles' },
  { value: 'bike', label: 'Bike' },
  { value: 'scooty', label: 'Scooty' },
  { value: 'auto', label: 'Auto' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'mini_truck', label: 'Mini Truck' },
  { value: 'three_wheeler', label: 'Three Wheeler' },
  { value: 'tata_ace', label: 'Tata Ace' },
  { value: 'pickup_8ft', label: 'Pickup 8ft' },
  { value: 'tata_407', label: 'Tata 407' },
];

const paymentOptions = [
  { value: 'all', label: 'All Payments' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
];

export function BookingsListPage() {
  const navigate = useNavigate();
  const { page, limit, setPage, setLimit } = usePagination();
  const { filters, setFilter, resetFilters } = useFilters({
    defaultValues: {
      search: '',
      status: 'all',
      vehicleType: 'all',
      paymentStatus: 'all',
    },
  });

  const [cancelDialog, setCancelDialog] = useState<{
    isOpen: boolean;
    booking: Booking | null;
  }>({ isOpen: false, booking: null });

  const { data, isLoading, refetch } = useBookings({
    search: filters.search,
    status: filters.status as 'all' | 'pending' | 'accepted' | 'driver_assigned' | 'ongoing' | 'completed' | 'cancelled' | undefined,
    vehicleType: filters.vehicleType as BookingsParams['vehicleType'],
    paymentStatus: filters.paymentStatus as 'all' | 'pending' | 'paid' | 'refunded' | undefined,
    page,
    limit,
  });

  const { mutate: cancelBooking, isPending: cancelling } = useCancelBooking();

  const handleCancel = () => {
    if (cancelDialog.booking) {
      cancelBooking(
        { id: cancelDialog.booking.id, reason: 'Cancelled by admin' },
        {
          onSuccess: () => setCancelDialog({ isOpen: false, booking: null }),
        }
      );
    }
  };

  const columns: ColumnDef<Booking>[] = [
    {
      accessorKey: 'bookingNumber',
      header: 'Booking ID',
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.bookingNumber}</span>
      ),
    },
    {
      accessorKey: 'customer',
      header: 'Customer',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar src={row.original.customer.avatar} alt={row.original.customer.name} size="xs" />
          <div>
            <p className="font-medium text-sm">{row.original.customer.name}</p>
            <p className="text-xs text-muted-foreground">{row.original.customer.phone}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'route',
      header: 'Route',
      cell: ({ row }) => (
        <div className="max-w-[200px]">
          <p className="text-sm truncate">{row.original.pickup.address.split(',')[0]}</p>
          <p className="text-xs text-muted-foreground truncate">
            → {row.original.dropoff.address.split(',')[0]}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'fare',
      header: 'Fare',
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{formatCurrency(row.original.fare.total)}</p>
          <PaymentStatusBadge status={row.original.payment.status} />
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <BookingStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm">
          <p>{formatDate(row.original.createdAt)}</p>
          <p className="text-xs text-muted-foreground">
            {formatRelativeTime(row.original.createdAt)}
          </p>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Dropdown
          trigger={
            <Button variant="ghost" size="icon-sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          }
        >
          <DropdownItem
            icon={<Eye className="h-4 w-4" />}
            onClick={() =>
              navigate(buildRoute(ROUTES.BOOKING_DETAIL, { id: row.original.id }))
            }
          >
            View Details
          </DropdownItem>
          {['driver_assigned', 'ongoing'].includes(row.original.status) && (
            <DropdownItem
              icon={<MapPin className="h-4 w-4" />}
              onClick={() =>
                navigate(buildRoute(ROUTES.LIVE_TRACKING, { id: row.original.id }))
              }
            >
              Track Live
            </DropdownItem>
          )}
          {!['completed', 'cancelled'].includes(row.original.status) && (
            <>
              <DropdownSeparator />
              <DropdownItem
                icon={<X className="h-4 w-4" />}
                destructive
                onClick={() => setCancelDialog({ isOpen: true, booking: row.original })}
              >
                Cancel Booking
              </DropdownItem>
            </>
          )}
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bookings"
        description="View and manage all delivery bookings"
      />

      <FilterBar
        filters={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search by ID, customer, or driver...',
            className: 'w-80',
          },
          {
            type: 'select',
            key: 'status',
            placeholder: 'Status',
            options: statusOptions,
          },
          {
            type: 'select',
            key: 'vehicleType',
            placeholder: 'Vehicle',
            options: vehicleOptions,
          },
          {
            type: 'select',
            key: 'paymentStatus',
            placeholder: 'Payment',
            options: paymentOptions,
          },
        ]}
        values={filters}
        onChange={(key, value) => setFilter(key as keyof typeof filters, value as string)}
        onReset={resetFilters}
        onRefresh={() => refetch()}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        showPagination={false}
        showSearch={false}
        onRowClick={(row) =>
          navigate(buildRoute(ROUTES.BOOKING_DETAIL, { id: row.id }))
        }
      />

      {data && (
        <TablePagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
          totalItems={data.pagination.total}
          pageSize={data.pagination.limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
        />
      )}

      <ConfirmDialog
        isOpen={cancelDialog.isOpen}
        onClose={() => setCancelDialog({ isOpen: false, booking: null })}
        onConfirm={handleCancel}
        title="Cancel Booking?"
        description={`Are you sure you want to cancel booking ${cancelDialog.booking?.bookingNumber}? This action cannot be undone.`}
        confirmText="Cancel Booking"
        variant="destructive"
        isLoading={cancelling}
      />
    </div>
  );
}
